"use client"

import { useState, useMemo, useEffect } from "react"
import Link from "next/link"
import {
  Container,
  Text,
  Anchor,
  SimpleGrid,
  Box,
  Group,
  Select,
  TextInput,
  Button,
  Paper,
  Stack,
  Drawer,
  Card,
  Table,
  ScrollArea,
  Grid,
  Divider,
} from "@forestsync/ui"
import { TbLeaf, TbFolders, TbCurrencyEuro, TbDatabase } from "@forestsync/ui"
import { useDisclosure } from "@mantine/hooks"
import { useTranslations } from "next-intl"
import { useRouter } from "next/navigation"
import { ProjectCard, type Sink } from "@/components/shared/ProjectCard"
import { BuyCreditsModal } from "@/components/browse/BuyCreditsModal"
import { LiteWizard } from "@/components/procurement/LiteWizard"
import type { MarketSummary, GlobalTradeRow } from "@/lib/queries"
import type { LandingMetrics } from "@/actions/metrics"
import { COUNTRY_TO_REGION, getCountryName } from "@forestsync/db"
import { createBrowserClient } from "@forestsync/auth/browser"

interface BrowseViewProps {
  sinks: Sink[]
  markets?: MarketSummary[]
  globalTrades?: GlobalTradeRow[]
  metrics?: LandingMetrics
  isGuest?: boolean
}

function getUniqueValues<T>(items: T[], key: keyof T): string[] {
  const seen = new Set<string>()
  for (const item of items) {
    const val = item[key]
    if (val !== null && val !== undefined) seen.add(String(val))
  }
  return [...seen].sort()
}

function formatRelativeTime(d: Date): string {
  const diffMs = Date.now() - d.getTime()
  const diffMins = Math.floor(diffMs / 60_000)
  if (diffMins < 1) return "just now"
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  return d.toLocaleDateString([], { month: "short", day: "numeric" })
}

export function BrowseView({ sinks, markets, globalTrades, metrics, isGuest }: BrowseViewProps) {
  // Live prices updated via Supabase Realtime
  const [liveSinks, setLiveSinks] = useState<Sink[]>(sinks)
  useEffect(() => {
    const supabase = createBrowserClient()
    const channel = supabase
      .channel("browse-fills")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "trade_orders" },
        (payload) => {
          const row = payload.new as Record<string, unknown> | null
          if (!row || row.status !== "FILLED") return
          const price = row.price != null ? Number(row.price) : null
          const refId = row.project_ref_id as string | null
          if (price === null || !refId) return
          setLiveSinks((prev) =>
            prev.map((s) => (s.refId === refId ? { ...s, lastPrice: price } : s)),
          )
        },
      )
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [])
  // Map projectRefId → latest vintage from active market data
  const refIdToVintage = useMemo<Map<string, number>>(() => {
    if (!markets) return new Map()
    const map = new Map<string, number>()
    for (const m of markets) {
      const existing = map.get(m.projectRefId)
      if (existing === undefined || m.vintage > existing) map.set(m.projectRefId, m.vintage)
    }
    return map
  }, [markets])

  // Map projectRefId → all active vintages (descending) — used by the vintage filter
  // so a project with vintages 2024+2025 matches a 2024 filter, not just its latest.
  const refIdToAllVintages = useMemo<Map<string, number[]>>(() => {
    const map = new Map<string, number[]>()
    if (!markets) return map
    for (const m of markets) {
      const arr = map.get(m.projectRefId)
      if (arr) {
        if (!arr.includes(m.vintage)) arr.push(m.vintage)
      } else {
        map.set(m.projectRefId, [m.vintage])
      }
    }
    for (const arr of map.values()) arr.sort((a, b) => b - a)
    return map
  }, [markets])

  // Map projectRefId → (vintage → MarketSummary) for vintage-specific price/depth lookup
  const refIdToMarketByVintage = useMemo<Map<string, Map<number, MarketSummary>>>(() => {
    const map = new Map<string, Map<number, MarketSummary>>()
    if (!markets) return map
    for (const m of markets) {
      let inner = map.get(m.projectRefId)
      if (!inner) {
        inner = new Map()
        map.set(m.projectRefId, inner)
      }
      inner.set(m.vintage, m)
    }
    return map
  }, [markets])
  const t = useTranslations("browse")
  const tNav = useTranslations("nav")
  const router = useRouter()

  const [filterCountry, setFilterCountry] = useState<string | null>(null)
  const [filterCreditType, setFilterCreditType] = useState<string | null>(null)
  const [filterVintage, setFilterVintage] = useState<string | null>(null)
  const [filterPriceMin, setFilterPriceMin] = useState("")
  const [filterPriceMax, setFilterPriceMax] = useState("")

  // Buy modal
  const [buyTarget, setBuyTarget] = useState<{ sink: Sink; vintage: number; creditType: string } | null>(null)

  // Find Credits wizard drawer
  const [wizardOpened, { open: openWizard, close: closeWizard }] = useDisclosure(false)

  const countries = useMemo(() => getUniqueValues(sinks, "country"), [sinks])
  const creditTypes = useMemo(
    () =>
      [...new Set(sinks.map((s) => s.creditType ?? s.methodology).filter((m): m is string => !!m))].sort(),
    [sinks]
  )
  const regions = useMemo(
    () =>
      [...new Set(sinks.map((s) => s.country && COUNTRY_TO_REGION[getCountryName(s.country)]).filter((r): r is string => !!r))].sort(),
    [sinks]
  )
  const vintages = useMemo(
    () => {
      const vSet = new Set<number>()
      if (markets) {
        markets.forEach(m => vSet.add(m.vintage))
      }
      return [...vSet].sort().map(String)
    },
    [markets]
  )

  const countryOptions = [
    { value: "", label: t("allCountries") },
    ...countries.map((c) => ({ value: c, label: getCountryName(c) })),
  ]

  const creditTypeOptions = [
    { value: "", label: t("allMethodologies") }, // Using existing translation key
    ...creditTypes.map((m) => ({ value: m, label: m })),
  ]

  const vintageOptions = [
    { value: "", label: t("allVintages") },
    ...vintages.map((v) => ({ value: v, label: v })),
  ]

  const filtered = useMemo(() => {
    return liveSinks.filter((sink) => {
      if (filterCountry && sink.country !== filterCountry) return false
      
      const cType = sink.creditType ?? sink.methodology
      if (filterCreditType && cType !== filterCreditType) return false
      
      if (filterVintage) {
        const projectVintages = refIdToAllVintages.get(sink.refId)
        if (!projectVintages || !projectVintages.includes(parseInt(filterVintage, 10))) return false
      }
      
      const priceMin = filterPriceMin ? parseFloat(filterPriceMin) : null
      const priceMax = filterPriceMax ? parseFloat(filterPriceMax) : null
      if (priceMin !== null && (sink.lastPrice === null || sink.lastPrice < priceMin)) return false
      if (priceMax !== null && (sink.lastPrice === null || sink.lastPrice > priceMax)) return false
      return true
    })
  }, [liveSinks, filterCountry, filterCreditType, filterVintage, filterPriceMin, filterPriceMax, refIdToAllVintages])

  const hasFilters = !!filterCountry || !!filterCreditType || !!filterVintage || !!filterPriceMin || !!filterPriceMax

  // Lowest current ask across all live markets — honest "entry price" for buyers,
  // avoids the misleading range where a buyer sees €55–190 then pays €200+.
  const startingFrom = useMemo(() => {
    const asks = (markets ?? []).filter((m) => m.bestAsk !== null).map((m) => m.bestAsk!)
    return asks.length === 0 ? null : Math.min(...asks)
  }, [markets])

  // Distinct projects that have at least one live ask (real, buyable supply).
  const projectsWithSupply = useMemo(() => {
    const set = new Set<string>()
    for (const m of markets ?? []) {
      if (m.bestAsk !== null) set.add(m.projectRefId)
    }
    return set.size
  }, [markets])

  const [filtersDrawerOpened, { open: openFiltersDrawer, close: closeFiltersDrawer }] = useDisclosure(false)

  function clearFilters() {
    setFilterCountry(null)
    setFilterCreditType(null)
    setFilterVintage(null)
    setFilterPriceMin("")
    setFilterPriceMax("")
  }

  function handleOpenWizard() {
    if (isGuest) {
      router.push("/register?intent=buy")
      return
    }
    openWizard()
  }

  return (
    <Box bg="var(--mantine-color-body)" mih="100vh" pt="md" pb="xl">
      <Container size="xl">
        {/* Compact toolbar */}
        <Group justify="space-between" align="center" mb="lg">
          <Group gap="xs" align="center">
            <Text fw={700} size="xl">{t("title")}</Text>
            <Text size="sm" c="dimmed">· {t("resultsCount", { count: filtered.length })}</Text>
          </Group>
          <Group gap="sm">
            <Anchor component={Link} href="/market/options" size="sm">{t("optionChainLink")}</Anchor>
            {/* Mobile: filters button */}
            <Box hiddenFrom="md">
              <Button variant="default" onClick={openFiltersDrawer} size="sm">
                {t("filtersMobile")}{hasFilters ? ` (${t("active")})` : ""}
              </Button>
            </Box>
          </Group>
        </Group>

        {/* Market stats strip — buyer-relevant: what's available and at what entry price */}
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm" mb="xl">
          <Card withBorder radius="md" p="sm">
            <Group gap={5} mb={4}>
              <Box c="green"><TbLeaf size={13} /></Box>
              <Text size="xs" c="dimmed">{t("statsProjectsWithSupply")}</Text>
            </Group>
            <Text fw={700} size="md" c="green">{projectsWithSupply}</Text>
          </Card>
          <Card withBorder radius="md" p="sm">
            <Group gap={5} mb={4}>
              <Box c="green"><TbCurrencyEuro size={13} /></Box>
              <Text size="xs" c="dimmed">{t("statsStartingFrom")}</Text>
            </Group>
            <Text fw={700} size="md" c={startingFrom !== null ? "green" : "dimmed"}>{startingFrom !== null ? `€${startingFrom.toFixed(2)} / tCO₂e` : t("soldOut")}</Text>
          </Card>
          <Card withBorder radius="md" p="sm">
            <Group gap={5} mb={4}>
              <Box c="green"><TbDatabase size={13} /></Box>
              <Text size="xs" c="dimmed">{t("statsAvailableCredits")}</Text>
            </Group>
            <Text fw={700} size="md" c="green">{metrics?.listedVolume != null ? `${Math.round(metrics.listedVolume).toLocaleString()} tCO₂e` : "N/A"}</Text>
          </Card>
          <Card withBorder radius="md" p="sm">
            <Group gap={5} mb={4}>
              <Box c="green"><TbFolders size={13} /></Box>
              <Text size="xs" c="dimmed">{t("statsProjectsListed")}</Text>
            </Group>
            <Text fw={700} size="md" c="green">{sinks.length}</Text>
          </Card>
        </SimpleGrid>

        {projectsWithSupply === 0 && sinks.length > 0 && (
          <Text size="sm" c="dimmed" ta="center" mb="xl">
            {t("allProjectsSoldOut")}{" "}
            <Anchor href="mailto:info@forestsync.com" size="sm" c="dimmed">info@forestsync.com</Anchor>
          </Text>
        )}

        <Grid gutter="xl">
          {/* Left Column: Project Grid */}
          <Grid.Col span={{ base: 12, md: 8, lg: 9 }}>
            {sinks.length === 0 ? (
              <Text c="dimmed" ta="center">{t("noProjects")}</Text>
            ) : filtered.length === 0 ? (
              <Text c="dimmed" ta="center">{t("noResults")}</Text>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
                {filtered.map((sink) => {
                  const allVintages = refIdToAllVintages.get(sink.refId)
                  const filterV = filterVintage ? parseInt(filterVintage, 10) : null
                  // Show the filtered vintage's data when filtering, else the latest vintage.
                  const effectiveVintage =
                    filterV !== null && allVintages?.includes(filterV)
                      ? filterV
                      : refIdToVintage.get(sink.refId)
                  const marketData =
                    effectiveVintage !== undefined
                      ? refIdToMarketByVintage.get(sink.refId)?.get(effectiveVintage)
                      : undefined
                  const sinkForCard: Sink = marketData
                    ? { ...sink, bestAsk: marketData.bestAsk, askVolume: marketData.askDepth }
                    : sink
                  return (
                    <ProjectCard
                      key={String(sink.id)}
                      sink={sinkForCard}
                      marketVintage={effectiveVintage}
                      vintageCount={allVintages?.length ?? 0}
                      isGuest={isGuest}
                    />
                  )
                })}
              </SimpleGrid>
            )}
          </Grid.Col>

          {/* Right Column: Sticky Sidebar */}
          <Grid.Col span={{ base: 12, md: 4, lg: 3 }}>
            <Box style={{ position: "sticky", top: 80 }}>
              
              {/* Concierge / Procurement CTA */}
              <Card withBorder radius="md" p="md" mb="xl" bg={"forest"} c="var(--mantine-color-body)">
                <Text fw={700} size="lg" mb={8}>{t("conciergeTitle")}</Text>
                <Text size="sm" mb="md" style={{ opacity: 0.9 }}>
                  {t("conciergeDesc")}
                </Text>
                <Button variant="white" c="dark" fullWidth onClick={handleOpenWizard}>
                  {t("conciergeCta")}
                </Button>
              </Card>

              {/* Vertical Filters */}
              <Paper withBorder p="md" radius="md" mb="xl" visibleFrom="sm">
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Text fw={600} size="sm">{t("filters")}</Text>
                    {hasFilters && (
                      <Button variant="subtle" color="gray" size="xs" onClick={clearFilters} style={{ padding: '0 8px', height: '24px' }}>
                        {t("clearFilters")}
                      </Button>
                    )}
                  </Group>
                  <Select
                    label={t("filterCountry")}
                    data={countryOptions}
                    value={filterCountry ?? ""}
                    onChange={(v) => setFilterCountry(v || null)}
                    clearable
                    searchable
                    comboboxProps={{ withinPortal: true }}
                  />
                  <Select
                    label={t("filterMethodology")}
                    data={creditTypeOptions}
                    value={filterCreditType ?? ""}
                    onChange={(v) => setFilterCreditType(v || null)}
                    clearable
                    searchable
                    comboboxProps={{ withinPortal: true }}
                  />
                  <Select
                    label={t("filterVintage")}
                    data={vintageOptions}
                    value={filterVintage ?? ""}
                    onChange={(v) => setFilterVintage(v || null)}
                    clearable
                    comboboxProps={{ withinPortal: true }}
                  />
                  <Group grow gap="xs">
                    <TextInput
                      label={t("filterPriceMin")}
                      type="number"
                      value={filterPriceMin}
                      onChange={(e) => setFilterPriceMin(e.currentTarget.value)}
                      placeholder="0"
                    />
                    <TextInput
                      label={t("filterPriceMax")}
                      type="number"
                      value={filterPriceMax}
                      onChange={(e) => setFilterPriceMax(e.currentTarget.value)}
                      placeholder="∞"
                    />
                  </Group>
                </Stack>
              </Paper>

              {/* Recent trades */}
              {globalTrades && globalTrades.length > 0 && (
                <Paper withBorder radius="md" p="md" mb="xl">
                  <Text fw={600} size="sm" mb="sm">{t("recentActivity")}</Text>
                  <Stack gap="sm">
                    {globalTrades.slice(0, 5).map((trade, i) => (
                      <Box key={i}>
                        <Group justify="space-between" align="flex-start" wrap="nowrap">
                          <Box style={{ flex: 1, minWidth: 0 }}>
                            <Text size="xs" fw={500} truncate>{trade.projectName}</Text>
                            <Text size="xs" c="dimmed" truncate>{trade.creditType} · {trade.vintage}</Text>
                          </Box>
                          <Box style={{ textAlign: "right", flexShrink: 0 }}>
                            <Text size="xs" fw={600} c="green">{trade.price != null ? `€${Number(trade.price).toFixed(2)}` : "N/A"}</Text>
                            <Text size="xs" c="dimmed">
                              {trade.volume.toLocaleString()} t
                            </Text>
                          </Box>
                        </Group>
                        <Text size="xs" c="dimmed" ta="right" mt={2} style={{ fontSize: "10px" }}>
                          {trade.filledAt ? formatRelativeTime(new Date(trade.filledAt)) : "N/A"}
                        </Text>
                        {i < Math.min(globalTrades.length, 5) - 1 && <Divider my={8} variant="dashed" />}
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              )}
            </Box>
          </Grid.Col>
        </Grid>
      </Container>

      {/* Mobile filter drawer */}
      <Drawer
        opened={filtersDrawerOpened}
        onClose={closeFiltersDrawer}
        title={t("filters")}
        position="bottom"
        size="auto"
        hiddenFrom="sm"
      >
        <Stack gap="sm" pb="xl">
          <Select
            label={t("filterCountry")}
            data={countryOptions}
            value={filterCountry ?? ""}
            onChange={(v) => setFilterCountry(v || null)}
            clearable
            searchable
            comboboxProps={{ withinPortal: true }}
          />
          <Select
            label={t("filterMethodology")}
            data={creditTypeOptions}
            value={filterCreditType ?? ""}
            onChange={(v) => setFilterCreditType(v || null)}
            clearable
            searchable
            comboboxProps={{ withinPortal: true }}
          />
          <Select
            label={t("filterVintage")}
            data={vintageOptions}
            value={filterVintage ?? ""}
            onChange={(v) => setFilterVintage(v || null)}
            clearable
            comboboxProps={{ withinPortal: true }}
          />
          <Group grow gap="sm">
            <TextInput
              label={t("filterPriceMin")}
              type="number"
              value={filterPriceMin}
              onChange={(e) => setFilterPriceMin(e.currentTarget.value)}
              placeholder="0"
            />
            <TextInput
              label={t("filterPriceMax")}
              type="number"
              value={filterPriceMax}
              onChange={(e) => setFilterPriceMax(e.currentTarget.value)}
              placeholder="∞"
            />
          </Group>
          <Group justify="space-between" mt="xs">
            {hasFilters && (
              <Button variant="subtle" color="gray" onClick={clearFilters} size="sm">
                {t("clearFilters")}
              </Button>
            )}
            <Button onClick={closeFiltersDrawer} ml="auto">
              {t("applyFilters")}
            </Button>
          </Group>
          <Text size="xs" c="dimmed">{t("resultsCount", { count: filtered.length })}</Text>
        </Stack>
      </Drawer>

      {/* Find Credits wizard drawer */}
      <Drawer
        opened={wizardOpened}
        onClose={closeWizard}
        title={tNav("procurement")}
        position="right"
        size="lg"
      >
        <LiteWizard methodologies={creditTypes} regions={regions} />
      </Drawer>

      {/* Buy credits modal */}
      <BuyCreditsModal
        sink={buyTarget?.sink ?? null}
        vintage={buyTarget?.vintage ?? 0}
        creditType={buyTarget?.creditType ?? ""}
        onClose={() => setBuyTarget(null)}
      />
    </Box>
  )
}
